export enum QuestionType {
	YES_NO = 'yes_no',
	RATING = 'rating',
	COMMENT = 'comment'
}

export enum RatingScale {
	POOR = 1,
	FAIR = 2,
	AVERAGE = 3,
	GOOD = 4,
	EXCELLENT = 5
}

export enum CampaignStatus {
	ACTIVE = 'active',
	PAST = 'past',
	SCHEDULED = 'scheduled'
}

export enum OwnershipType {
	WORKPLACE = 'workplace',
	ORGANIZATION = 'organization'
}

export interface ICampaignQuestion {
	id: string;
	questionText: string;
	questionType: QuestionType;
	sortOrder: number;
	createdAt: string;
	updatedAt: string;
}

export interface ICampaignReward {
	'@id': string;
	rewardName: string;
	numberOfWinners: number;
	rewardDetailsText?: string;
	enabled: boolean;
}

export interface ICampaign {
	id: string;
	title: string;
	ownershipType: OwnershipType;
	organizationId: string;
	workplaceId?: string;
	startDate: string;
	endDate: string;
	questions: ICampaignQuestion[];
	reward?: ICampaignReward;
	uniqueUrl: string;
	qrCode?: string;
	status: CampaignStatus;
	createdAt: string;
	updatedAt: string;
	createdBy: string;
}

export interface ICampaignResponse {
	id: string;
	campaignId: string;
	workplaceId: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	answers: ICampaignAnswer[];
	comment?: string;
	submittedAt: string;
	isWinner?: boolean;
}

export interface ICampaignAnswer {
	questionId: string;
	questionText: string;
	questionType: QuestionType;
	answer: boolean | number | string; // boolean for yes/no, number (1-5) for rating, string for comment
}

export interface ICampaignResults {
	campaign: ICampaign;
	totalResponses: number;
	questionResults: IQuestionResult[];
	winners?: ICampaignWinner[];
	rewardOptInCount?: number;
}

export interface IQuestionResult {
	questionId: string;
	questionText: string;
	questionType: QuestionType;
	// For yes/no questions
	yesCount?: number;
	noCount?: number;
	yesPercentage?: number;
	noPercentage?: number;
	// For rating questions
	averageRating?: number;
	// For comment questions
	comments?: string[];
	ratingDistribution?: {
		[key in RatingScale]?: number;
	};
	totalAnswers: number;
}

export interface ICampaignWinner {
	id: string;
	campaignId: string;
	responseId: string;
	firstName?: string;
	lastName?: string;
	email?: string;
	phone?: string;
	selectedAt: string;
}

export interface IFeedbackWinner {
	'@id': string;
	'@type': string;
	id: number;
	campaign: string;
	submission: string;
	selectedAt: string;
}

export interface IFeedbackSubmissionAnswer {
	'@type': string;
	'@id': string;
	id: number;
	submission: string;
	question: {
		'@type': string;
		'@id': string;
		id: number;
		questionText: string;
		questionType: QuestionType;
		sortOrder: number;
	};
	value: string;
	metadataJson: any;
}

export interface IFeedbackSubmission {
	'@id': string;
	'@type': string;
	id: number;
	campaign: {
		'@id': string;
		'@type': string;
		id: number;
		title: string;
		startDate: string;
		endDate: string;
		reward: string;
		winnersCount: number;
		uniqueUrlToken: string;
		questions: Array<{
			'@type': string;
			'@id': string;
			id: number;
			questionText: string;
			questionType: QuestionType;
			sortOrder: number;
		}>;
	};
	ipAddress: string;
	userAgent: string;
	browser: string;
	device: string;
	name: string;
	email: string;
	phone: string;
	rewardOptIn: boolean;
	metadataJson: Array<{
		timestamp: string;
		browser: string;
		device: string;
		userAgent: string;
	}>;
	workplace: string;
	submittedAt: string;
	answers: IFeedbackSubmissionAnswer[];
}

export interface ICampaignMetrics {
	totalCampaigns: number;
	activeCampaigns: number;
	totalResponses: number;
	averageResponseRate: number;
	averageSentimentScore: number;
	averageRating: number;
}

export interface ICampaignFormData {
	title: string;
	workplaceId: string;
	workplace?: string;
	startDate: string;
	endDate: string;
	questions: Omit<ICampaignQuestion, 'id' | 'createdAt' | 'updatedAt'>[];
	reward?: Omit<ICampaignReward, 'rewardId'> & { rewardId?: string };
}
